import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type ButtonTransitionPhase = 'idle' | 'exiting' | 'success' | 'entering';

export type UseButtonTransitionOptions = {
    /** How long the success state is visible (ms). Default: 500 */
    holdDuration?: number;
    /** Duration of slide animations (ms). Default: 120 */
    transitionDuration?: number;
    /** Callback when the full transition completes */
    onComplete?: () => void;
};

export type UseButtonTransitionReturn = {
    /** Current animation phase */
    phase: ButtonTransitionPhase;
    /** Trigger the transition */
    trigger: () => void;
    /** Reset immediately back to idle */
    reset: () => void;
    /** Whether any animation is in progress */
    isActive: boolean;
    /** Data attributes and CSS variables shared by transition renderers */
    transitionProps: {
        'data-button-transition': true;
        'data-button-transition-phase': ButtonTransitionPhase;
        style: CSSProperties;
    };
};

const DEFAULT_HOLD_DURATION = 500;
const DEFAULT_TRANSITION_DURATION = 120;

/** Manages the timed button transition phase sequence. */
export const useButtonTransition = (options: UseButtonTransitionOptions = {}): UseButtonTransitionReturn => {
    const {
        holdDuration = DEFAULT_HOLD_DURATION,
        transitionDuration = DEFAULT_TRANSITION_DURATION,
        onComplete,
    } = options;
    const [phase, setPhase] = useState<ButtonTransitionPhase>('idle');
    const phaseRef = useRef<ButtonTransitionPhase>('idle');
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const clearTransitionTimer = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
    }, []);

    const setTransitionPhase = useCallback((nextPhase: ButtonTransitionPhase) => {
        phaseRef.current = nextPhase;
        setPhase(nextPhase);
    }, []);

    const queuePhase = useCallback(
        (nextPhase: ButtonTransitionPhase, delay: number, afterPhase?: () => void) => {
            timeoutRef.current = setTimeout(() => {
                setTransitionPhase(nextPhase);
                afterPhase?.();
            }, delay);
        },
        [setTransitionPhase],
    );

    const reset = useCallback(() => {
        clearTransitionTimer();
        setTransitionPhase('idle');
    }, [clearTransitionTimer, setTransitionPhase]);

    useEffect(() => clearTransitionTimer, [clearTransitionTimer]);

    const trigger = useCallback(() => {
        if (phaseRef.current !== 'idle') return;

        clearTransitionTimer();
        setTransitionPhase('exiting');

        queuePhase('success', transitionDuration, () => {
            queuePhase('entering', holdDuration, () => {
                queuePhase('idle', transitionDuration, () => onCompleteRef.current?.());
            });
        });
    }, [clearTransitionTimer, holdDuration, queuePhase, setTransitionPhase, transitionDuration]);

    return {
        phase,
        trigger,
        reset,
        isActive: phase !== 'idle',
        transitionProps: {
            'data-button-transition': true,
            'data-button-transition-phase': phase,
            style: { '--button-transition-duration': `${transitionDuration}ms` } as CSSProperties,
        },
    };
};
